import React, { useRef, useEffect } from "react";

// A row of individual single-character boxes for entering a numeric
// code (a TOTP 6-digit code) — auto-advances focus to the next box as
// each digit is typed, backspace on an empty box moves back to the
// previous one, pasting a full code fills every box at once, and
// arrow keys move between boxes. Calls onComplete with the full code
// the moment every box is filled, so the parent can auto-submit
// without needing a separate button click — but a submit button
// should still exist alongside this for anyone who edits a digit
// after the row's already full (nothing re-fires onComplete just from
// changing an already-complete code).
export default function OtpCodeInput({ length = 6, value, onChange, onComplete, autoFocus, disabled }) {
  const inputRefs = useRef([]);
  const digits = value.split("").slice(0, length);
  while (digits.length < length) digits.push("");

  useEffect(() => {
    if (autoFocus) inputRefs.current[0]?.focus();
    // Only on mount — this shouldn't re-steal focus every time value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDigit = (index, char) => {
    const next = [...digits];
    next[index] = char;
    const nextValue = next.join("");
    onChange(nextValue);
    if (nextValue.length === length && next.every((d) => d !== "")) {
      onComplete?.(nextValue);
    }
  };

  const handleChange = (index, rawInput) => {
    // Handles both a normal single keystroke and a multi-character
    // paste landing in one box (some browsers/autofill put the whole
    // clipboard contents into whichever box currently has focus,
    // rather than firing a distinct paste event) — either way, only
    // digits count, and anything beyond the first character here
    // spills into the following boxes.
    const cleaned = rawInput.replace(/\D/g, "");
    if (!cleaned) {
      setDigit(index, "");
      return;
    }
    // A paste (more than one character) always fills from the start,
    // regardless of which box currently has focus — both the more
    // intuitive behavior, and required for correctness: the digits
    // array gets represented as a single joined string elsewhere in
    // this component, which silently loses any gap before a
    // mid-position paste (['','','','','7','8'] and
    // ['7','8','','','',''] would otherwise both collapse to "78").
    const startIndex = cleaned.length > 1 ? 0 : index;
    const next = [...digits];
    let i = startIndex;
    for (const ch of cleaned) {
      if (i >= length) break;
      next[i] = ch;
      i++;
    }
    const nextValue = next.join("");
    onChange(nextValue);
    const lastFilledIndex = Math.min(startIndex + cleaned.length, length) - 1;
    if (lastFilledIndex < length - 1) {
      inputRefs.current[lastFilledIndex + 1]?.focus();
    } else {
      inputRefs.current[length - 1]?.focus();
    }
    if (nextValue.length === length && next.every((d) => d !== "")) {
      onComplete?.(nextValue);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        setDigit(index, "");
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        setDigit(index - 1, "");
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      e.preventDefault();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      e.preventDefault();
    }
  };

  return (
    <div style={{ display: "flex", gap: 8 }}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={length} // generous on purpose — see handleChange's own paste-into-one-box handling
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={(e) => e.target.select()}
          style={{
            width: 40,
            height: 48,
            textAlign: "center",
            fontSize: 20,
            background: "var(--bg-input)",
            border: "1px solid var(--border-color-light)",
            borderRadius: 6,
            color: "var(--text-primary)",
          }}
        />
      ))}
    </div>
  );
}
